// Core packages
import { Injectable } from "@angular/core";

// Third party packages
import { Observable, throwError } from "rxjs";
import { catchError } from "rxjs/operators";

// Custom packages
import { ListApiResponseV2 } from "src/app/shared/interfaces/_base/api-response.interface";
import { ApiService } from "src/app/shared/services/api.service";
import { ConfigService } from "src/app/shared/services/config.service";
import { HelperService } from "src/app/shared/services/helper.service";

@Injectable({
  providedIn: "root",
})
export class RolesService {
  roleTypes: { label: string; value: string }[] =
    this.configService.availableRoleTypes;

  /**
   * Class constructor
   */
  constructor(
    private apiService: ApiService,
    private helperService: HelperService,
    private configService: ConfigService
  ) {}

  /**
   * Create a new role
   *
   * @since 1.0.0
   *
   * @param role Role data
   * @returns Observable<any>
   */
  create(role: any): Observable<any> {
    return this.apiService.apiCall(
      "POST",
      "roles",
      role,
      undefined,
      undefined,
      undefined,
      "v2"
    );
  }

  /**
   * Get roles list for given query params
   *
   * @since 1.0.0
   *
   * @param [start] Query left offset
   * @param [limit] Results number
   * @param [sort] Sorting field name
   * @param [sortVersus] Sorting versus. Can be 'asc' or 'desc'
   * @param [search] Filtering object with fildering data string
   */
  getList(
    start?: number,
    limit?: number,
    sort?: string,
    sortVersus?: string,
    search?: any
  ): Observable<ListApiResponseV2> {
    const params: any = { start, limit, sort, sortVersus };
    if (search) {
      Object.keys(search).forEach((key) => {
        const newParamKey = `search[${key}]`;
        const newParamValue = search[key];
        params[newParamKey] = newParamValue;
      });
    }
    return this.apiService.apiCall(
      "GET",
      "roles",
      params,
      undefined,
      undefined,
      undefined,
      "v2"
    );
  }

  /**
   * Get an exportation file of all roles
   *
   * @since 1.0.0
   */
  getExport(): Observable<any> {
    return this.apiService
      .apiCall(
        "GET",
        "roles/export",
        {},
        { responseType: "blob" },
        undefined,
        undefined,
        "v2"
      )
      .pipe(
        catchError((err) => {
          return this.helperService.parseErrorBlob(err);
        })
      );
  }

  /**
   * Get given roleId data from back-end
   *
   * @since 1.0.0
   *
   * @param roleId ID of the role to get
   */
  get(roleId: string): Observable<any> {
    return this.apiService
      .apiCall(
        "GET",
        `roles/${roleId}`,
        undefined,
        undefined,
        undefined,
        undefined,
        "v2"
      )
      .pipe(
        catchError((err) => {
          this.helperService.handleError(err);
          return throwError(err);
        })
      );
  }

  /**
   * Update role with given roleId with given role data
   *
   * @since 1.0.0
   *
   * @param roleId ID of role to update
   * @param data New permission's data
   */
  update(roleId: string, data: object): Observable<any> {
    return this.apiService.apiCall(
      "PUT",
      `roles/${roleId}`,
      data,
      undefined,
      undefined,
      undefined,
      "v2"
    );
  }

  /**
   * Delete permanently a role
   *
   * @since 1.0.0
   *
   * @param roleId ID of the role
   */
  delete(roleId: string): Observable<any> {
    return this.apiService.apiCall(
      "DELETE",
      `roles/${roleId}`,
      undefined,
      undefined,
      undefined,
      undefined,
      "v2"
    );
  }
}
